import React, { useEffect, useMemo, useState } from "react";
import { fetchTickets, fetchTicket, createTicket, addTicketMessage, setTicketStatus, getRole, getVenueType } from "../services/api";
import { useT } from "../i18n/useT";

// Help & Support centre — find help, submit a structured request, and follow the
// conversation. Trilingual for the primary labels; the long option lists render
// in English this pass (AM/OM fast-follow). Attachments deferred (no general
// uploader yet). Reuses the ODRA visual identity: navy headings, teal actions,
// warm off-white background, white rounded cards.
const S = {
  en: {
    title: "Help & Support", subtitle: "Find answers, report a problem, or contact the ODRA support team.",
    search: "Search setup guides, features, and common problems…",
    view_help: "View help", submit_title: "Submit a support request",
    subject: "Subject", req_type: "Request type", module: "Affected module", priority: "Priority",
    desc: "Description", desc_ph: "Tell us what happened, what you expected to happen, when the problem started, and any steps that may help us reproduce it.",
    ref: "Related order, reservation, booking, invoice, room, table or transaction number",
    ref_ph: "Order, booking, invoice, room, table…", when: "Date & approximate time",
    area: "Affected area", device: "Device", users: "Number of users affected",
    logs: "Include relevant technical information and application logs to help ODRA investigate this issue.",
    submit: "Submit request", submitting: "Submitting…", req_field: "Subject and description are required.",
    err: "Could not submit. Please try again.",
    crit_warn: "Critical priority should only be used when an essential operation — ordering, front desk, kitchen, payments or room management — is completely unavailable.",
    yours: "Your requests", search_tickets: "Search tickets…", all_status: "All status", all_priority: "All priority", all_module: "All modules",
    none: "No support requests yet.", none_sub: "When you submit a request, you can follow its status and replies here.",
    back: "← All requests", reply: "Reply", reply_ph: "Type your reply…", send: "Send reply",
    resolve: "Mark resolved", reopen: "Reopen", waiting: "ODRA is waiting for your response.",
    created: "Created", updated: "Updated", assigned: "Assigned to", details: "Request details",
    guides: "User Guides", guides_d: "Step-by-step instructions for using ODRA modules.", browse: "Browse guides",
    videos: "Video Training", videos_d: "Short training videos for operational staff and managers.", watch: "Watch videos",
    status_c: "System Status", status_d: "Current availability of ODRA services and known incidents.", view_status: "View status",
    news: "What's New", news_d: "Recent features, improvements and important product changes.", see_news: "See what's new",
    still: "Still need help?", still_d: "Our support team is here for you.",
  },
  am: {
    title: "እገዛ እና ድጋፍ", subtitle: "መልስ ያግኙ፣ ችግር ያመልክቱ ወይም የ ODRA ድጋፍ ቡድንን ያግኙ።",
    search: "የማዋቀሪያ መመሪያዎችን፣ ባህሪዎችን እና የተለመዱ ችግሮችን ይፈልጉ…",
    view_help: "እገዛ ይመልከቱ", submit_title: "የድጋፍ ጥያቄ ያስገቡ",
    subject: "ርዕስ", req_type: "የጥያቄ ዓይነት", module: "የተጎዳ ክፍል", priority: "ቅድሚያ",
    desc: "መግለጫ", desc_ph: "ምን እንደተከሰተ፣ ምን እንደጠበቁ፣ ችግሩ መቼ እንደጀመረ ይንገሩን።",
    ref: "ተዛማጅ ትዕዛዝ፣ ቦታ ማስያዝ፣ ደረሰኝ፣ ክፍል ወይም ጠረጴዛ ቁጥር",
    ref_ph: "ትዕዛዝ፣ ማስያዝ፣ ደረሰኝ፣ ክፍል…", when: "ቀን እና ግምታዊ ሰዓት",
    area: "የተጎዳ አካባቢ", device: "መሣሪያ", users: "የተጎዱ ተጠቃሚዎች ብዛት",
    logs: "ODRA ችግሩን እንዲመረምር ተዛማጅ ቴክኒካዊ መረጃ ያካትቱ።",
    submit: "ጥያቄ አስገባ", submitting: "በመላክ ላይ…", req_field: "ርዕስና መግለጫ ያስፈልጋሉ።",
    err: "ማስገባት አልተቻለም። እንደገና ይሞክሩ።",
    crit_warn: "ወሳኝ ቅድሚያ የሚጠቀሙት አንድ አስፈላጊ ተግባር — ትዕዛዝ፣ የፊት ዴስክ፣ ኩሽና፣ ክፍያ ወይም ክፍል አስተዳደር — ሙሉ በሙሉ ሲቋረጥ ብቻ ነው።",
    yours: "የእርስዎ ጥያቄዎች", search_tickets: "ጥያቄዎችን ይፈልጉ…", all_status: "ሁሉም ሁኔታ", all_priority: "ሁሉም ቅድሚያ", all_module: "ሁሉም ክፍል",
    none: "እስካሁን ጥያቄ የለም።", none_sub: "ጥያቄ ሲያስገቡ ሁኔታውንና መልሶችን እዚህ መከታተል ይችላሉ።",
    back: "← ሁሉም ጥያቄዎች", reply: "መልስ", reply_ph: "መልስዎን ይጻፉ…", send: "መልስ ላክ",
    resolve: "እንደተፈታ ምልክት", reopen: "እንደገና ክፈት", waiting: "ODRA መልስዎን እየጠበቀ ነው።",
    created: "የተፈጠረ", updated: "የተዘመነ", assigned: "የተመደበለት", details: "የጥያቄ ዝርዝር",
    guides: "የተጠቃሚ መመሪያዎች", guides_d: "የ ODRA ክፍሎችን ለመጠቀም ደረጃ በደረጃ መመሪያ።", browse: "መመሪያዎችን ይመልከቱ",
    videos: "የቪዲዮ ስልጠና", videos_d: "ለሠራተኞችና አስተዳዳሪዎች አጭር የስልጠና ቪዲዮዎች።", watch: "ቪዲዮዎችን ይመልከቱ",
    status_c: "የስርዓት ሁኔታ", status_d: "የ ODRA አገልግሎቶች ተገኝነት።", view_status: "ሁኔታ ይመልከቱ",
    news: "አዲስ ነገር", news_d: "የቅርብ ጊዜ ባህሪዎችና ማሻሻያዎች።", see_news: "አዲሱን ይመልከቱ",
    still: "አሁንም እገዛ ይፈልጋሉ?", still_d: "የድጋፍ ቡድናችን ለእርስዎ ዝግጁ ነው።",
  },
  om: {
    title: "Gargaarsa & Deeggarsa", subtitle: "Deebii argadhu, rakkoo gabaasi, yookiin garee deeggarsa ODRA qunnami.",
    search: "Qajeelfama, amaloota fi rakkoolee beekamoo barbaadi…",
    view_help: "Gargaarsa ilaali", submit_title: "Gaaffii deeggarsaa galchi",
    subject: "Mata-duree", req_type: "Gosa gaaffii", module: "Moojuulii miidhame", priority: "Dursa",
    desc: "Ibsa", desc_ph: "Maaltu akka ta'e, maal akka eegde, yoom akka jalqabe nutti himi.",
    ref: "Lakk. ajaja, qabannoo, nagahee, kutaa yookiin minjaala walqabatu",
    ref_ph: "Ajaja, qabannoo, nagahee, kutaa…", when: "Guyyaa fi yeroo tilmaamaa",
    area: "Naannoo miidhame", device: "Meeshaa", users: "Baay'ina fayyadamtoota miidhaman",
    logs: "ODRA rakkoo qoratuuf odeeffannoo teeknikaa dabaladhu.",
    submit: "Gaaffii galchi", submitting: "Ergaa jira…", req_field: "Mata-dureen fi ibsi barbaachisu.",
    err: "Galchuu hin dandeenye. Irra deebi'i yaali.",
    crit_warn: "Dursi cimaan yeroo hojiin barbaachisaan — ajaja, fuula duraa, kushinaa, kaffaltii yookiin bulchiinsa kutaa — guutumaan guutuutti hin hojjenne qofa fayyadami.",
    yours: "Gaaffiiwwan kee", search_tickets: "Gaaffiiwwan barbaadi…", all_status: "Haala hunda", all_priority: "Dursa hunda", all_module: "Moojuulii hunda",
    none: "Ammatti gaaffiin hin jiru.", none_sub: "Yeroo gaaffii galchitu, haala fi deebii asitti hordofta.",
    back: "← Gaaffiiwwan hunda", reply: "Deebii", reply_ph: "Deebii kee barreessi…", send: "Deebii ergi",
    resolve: "Furame jedhii", reopen: "Deebisii bani", waiting: "ODRA deebii kee eegaa jira.",
    created: "Uumame", updated: "Haaromfame", assigned: "Kan itti kenname", details: "Ibsa gaaffii",
    guides: "Qajeelfama Fayyadamaa", guides_d: "Moojuulota ODRA fayyadamuuf qajeelfama tarkaanfiin.", browse: "Qajeelfama ilaali",
    videos: "Leenjii Viidiyoo", videos_d: "Viidiyoo leenjii gabaabaa hojjettootaaf.", watch: "Viidiyoo ilaali",
    status_c: "Haala Sirnaa", status_d: "Argama tajaajila ODRA.", view_status: "Haala ilaali",
    news: "Wanti Haaraa", news_d: "Amaloota fi fooyya'iinsa dhiyoo.", see_news: "Haaraa ilaali",
    still: "Ammas gargaarsa barbaadda?", still_d: "Gareen deeggarsaa nu bira jira.",
  },
};

// Option lists (EN this pass). value -> label.
const REQ_TYPES = [["not_working", "Something is not working"], ["how_to", "How do I use a feature?"], ["access", "Account or user access"], ["data", "Data or report issue"], ["billing", "Payment or billing question"], ["feature", "Feature request"], ["training", "Training request"], ["integration", "Integration request"], ["other", "Other"]];
const MODULES = [["ordering", "Ordering & Digital Menu"], ["kitchen", "Kitchen Operations"], ["pos", "POS & Payments"], ["front_desk", "Front Desk & Reservations"], ["housekeeping", "Housekeeping"], ["wellness", "Wellness & Services"], ["cost_control", "F&B Cost Control"], ["inventory", "Inventory & Purchasing"], ["reports", "Reports & Analytics"], ["users", "Users & Permissions"], ["general", "General"], ["other", "Other"]];
const PRIORITIES = [["low", "Low", "General question or minor issue."], ["normal", "Normal", "An issue exists, but work can continue."], ["high", "High", "An important operation is seriously affected."], ["critical", "Critical", "A core hotel or restaurant operation has stopped."]];
const AREAS = [["property", "Entire property"], ["front_desk", "Front desk"], ["restaurant", "Restaurant"], ["kitchen", "Kitchen"], ["bar", "Bar"], ["room_service", "Room service"], ["housekeeping", "Housekeeping"], ["wellness", "Wellness"], ["management", "Management"], ["other", "Other"]];
const DEVICES = [["desktop", "Desktop computer"], ["laptop", "Laptop"], ["tablet", "Tablet"], ["mobile", "Mobile phone"], ["pos", "POS terminal"], ["other", "Other"]];
const USERS_AFFECTED = [["one", "One user"], ["several", "Several users"], ["department", "One department"], ["property", "Entire property"], ["unknown", "Unknown"]];

// Help categories: [key, emoji, title, desc, roles-that-prioritise-it]
const CATEGORIES = [
  ["getting_started", "🚀", "Getting Started", "Learn the basics of ODRA", []],
  ["ordering", "📱", "QR Menu & Ordering", "QR menu, orders and tables", ["waiter", "cashier"]],
  ["kitchen", "👨‍🍳", "Kitchen & Order Status", "Kitchen workflow and order status", ["chef"]],
  ["front_desk", "🛎️", "Front Desk & Reservations", "Reservations, rooms and guests", ["front_desk"]],
  ["housekeeping", "🧹", "Housekeeping", "Room readiness and cleaning", ["front_desk"]],
  ["wellness", "🌿", "Wellness & Service Bookings", "Service bookings and schedules", []],
  ["cost_control", "📊", "F&B Cost Control", "Recipes, usage and variance", ["controller", "manager"]],
  ["inventory", "📦", "Inventory & Purchasing", "Stock, suppliers and orders", ["controller"]],
  ["reports", "📈", "Reports & Dashboards", "Analytics and performance", ["manager", "controller"]],
  ["users", "🔑", "Users, Roles & Permissions", "Staff accounts and access", ["manager"]],
];

const STATUS_LABEL = { submitted: "Submitted", received: "Received", investigating: "Investigating", waiting_customer: "Waiting for customer", solution_provided: "Solution provided", resolved: "Resolved", closed: "Closed", open: "Submitted", in_progress: "Investigating" };
const STATUS_COLOR = { submitted: "#0C447C", received: "#0C447C", investigating: "#854F0B", waiting_customer: "#6D28D9", solution_provided: "#1D7A44", resolved: "#1D7A44", closed: "#8a949b", open: "#0C447C", in_progress: "#854F0B" };
const PRIO_COLOR = { low: "#8a949b", normal: "#0C447C", high: "#854F0B", critical: "#A32D2D" };

const PAGE = { maxWidth: 1180, margin: "0 auto" };
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: 10, borderRadius: 9, border: "1px solid #d8dee2", fontSize: 14, width: "100%", boxSizing: "border-box" };
const lbl = { fontSize: 12.5, fontWeight: 600, color: "#334155", display: "block", margin: "10px 0 4px" };

function Badge({ text, color }) {
  return <span style={{ fontSize: 11.5, fontWeight: 700, color, background: color + "1A", padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>{text}</span>;
}

export default function SupportPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const role = getRole() || "manager";

  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fPrio, setFPrio] = useState("");
  const [fModule, setFModule] = useState("");
  const [catQuery, setCatQuery] = useState("");
  const empty = { subject: "", request_type: "not_working", affected_module: "general", priority: "normal", description: "", reference_number: "", incident_at: "", affected_area: "", device_type: "", users_affected: "", include_logs: false };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => fetchTickets().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  // role-aware category ordering
  const categories = useMemo(() => {
    const isGym = getVenueType() === "spa_gym";
    // Spa/gym venues don't run F&B, rooms or housekeeping — hide those help topics.
    const HIDE_GYM = ["ordering", "kitchen", "front_desk", "housekeeping", "cost_control"];
    const src = isGym ? CATEGORIES.filter((c) => !HIDE_GYM.includes(c[0])) : CATEGORIES;
    const scored = src.map((c, i) => [c, (c[4].includes(role) ? 0 : 1), i]);
    scored.sort((a, b) => a[1] - b[1] || a[2] - b[2]);
    let list = scored.map((s) => s[0]);
    if (catQuery.trim()) {
      const s = catQuery.toLowerCase();
      list = list.filter((c) => (c[2] + " " + c[3]).toLowerCase().includes(s));
    }
    return list;
  }, [role, catQuery]);

  const filtered = useMemo(() => {
    let r = rows || [];
    if (q.trim()) { const s = q.toLowerCase(); r = r.filter((x) => (x.subject + " " + (x.ticket_number || "")).toLowerCase().includes(s)); }
    if (fStatus) r = r.filter((x) => x.status === fStatus);
    if (fPrio) r = r.filter((x) => x.priority === fPrio);
    if (fModule) r = r.filter((x) => x.affected_module === fModule);
    return r;
  }, [rows, q, fStatus, fPrio, fModule]);

  const openThread = async (id) => { setOpenId(id); setThread(null); setReply(""); try { setThread(await fetchTicket(id)); } catch { setThread(null); } };
  const submit = async () => {
    setErr("");
    if (!form.subject.trim() || !form.description.trim()) { setErr(t("req_field")); return; }
    setBusy(true);
    try {
      await createTicket({
        subject: form.subject.trim(), body: form.description.trim(),
        request_type: form.request_type, affected_module: form.affected_module, priority: form.priority,
        reference_number: form.reference_number.trim() || null,
        incident_at: form.incident_at ? new Date(form.incident_at).toISOString() : null,
        affected_area: form.affected_area || null, device_type: form.device_type || null,
        users_affected: form.users_affected || null, include_logs: form.include_logs,
        context_path: window.location.pathname,
      });
      setForm(empty); load();
    } catch { setErr(t("err")); } finally { setBusy(false); }
  };
  const sendReply = async () => { if (!reply.trim()) return; setBusy(true); try { const d = await addTicketMessage(openId, reply.trim()); setReply(""); setThread(d); load(); } catch { /* */ } finally { setBusy(false); } };
  const changeStatus = async (status) => { try { const d = await setTicketStatus(openId, status); setThread(d); load(); } catch { /* */ } };

  const moduleLabel = (v) => (MODULES.find((m) => m[0] === v) || [null, v])[1];

  // ---------- detail view ----------
  if (openId && thread) {
    const st = thread.status;
    return (
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button onClick={() => { setOpenId(null); setThread(null); }} style={{ background: "none", border: "none", color: "#0F6E56", cursor: "pointer", fontSize: 14, padding: 0 }}>{t("back")}</button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 2px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{thread.subject}</h1>
          <Badge text={STATUS_LABEL[st] || st} color={STATUS_COLOR[st] || "#8a949b"} />
          <Badge text={(thread.priority || "").toUpperCase()} color={PRIO_COLOR[thread.priority] || "#8a949b"} />
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          {thread.ticket_number} · {moduleLabel(thread.affected_module)}
          {thread.assigned_to ? ` · ${t("assigned")}: ${thread.assigned_to}` : ""}
          {thread.reference_number ? ` · #${thread.reference_number}` : ""}
        </div>
        {st === "waiting_customer" && (
          <div style={{ background: "#F1ECFB", border: "1px solid #E0D6F7", color: "#5B21B6", borderRadius: 9, padding: "8px 12px", fontSize: 13, marginBottom: 10 }}>{t("waiting")}</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(thread.messages || []).map((m) => (
            <div key={m.id} style={{ ...CARD, padding: "10px 14px", alignSelf: m.author_type === "support" ? "flex-start" : "flex-end", maxWidth: "85%", background: m.author_type === "support" ? "#E1F5EE" : "#fff" }}>
              <div style={{ fontSize: 12, color: "#5b6472", marginBottom: 3 }}>{m.author_type === "support" ? "ODRA Support" : (m.author_name || "You")} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
              <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{m.body}</div>
            </div>
          ))}
        </div>
        <div style={{ ...CARD, padding: 12, marginTop: 14 }}>
          <textarea style={{ ...inp, minHeight: 70, resize: "vertical" }} placeholder={t("reply_ph")} value={reply} onChange={(e) => setReply(e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="btn primary small" disabled={busy} onClick={sendReply}>{t("send")}</button>
            {st !== "resolved" && st !== "closed"
              ? <button className="btn secondary small" onClick={() => changeStatus("resolved")}>{t("resolve")}</button>
              : <button className="btn secondary small" onClick={() => changeStatus("received")}>{t("reopen")}</button>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- main view ----------
  const helpCard = (c) => (
    <div key={c[0]} style={{ ...CARD, padding: 14, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 22 }}>{c[1]}</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#063b48", lineHeight: 1.15 }}>{c[2]}</span>
      </div>
      <div className="muted" style={{ fontSize: 12.5, minHeight: 32 }}>{c[3]}</div>
      <a href="#help" onClick={(e) => e.preventDefault()} style={{ fontSize: 13, color: "#0F6E56", fontWeight: 600, textDecoration: "none" }}>{t("view_help")} →</a>
    </div>
  );

  return (
    <div style={PAGE}>
      {/* header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, color: "#063b48" }}>{t("title")}</h1>
          <div className="muted" style={{ fontSize: 14.5, marginTop: 4 }}>{t("subtitle")}</div>
        </div>
        <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden="true">🎧</div>
      </div>

      {/* search */}
      <div style={{ ...CARD, padding: "4px 14px", marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 16, color: "#8a949b" }} aria-hidden="true">🔍</span>
        <input aria-label={t("search")} placeholder={t("search")} value={catQuery} onChange={(e) => setCatQuery(e.target.value)} style={{ border: "none", outline: "none", padding: "10px 0", fontSize: 14, width: "100%", background: "transparent" }} />
      </div>

      {/* category cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginTop: 14 }}>
        {categories.map(helpCard)}
      </div>

      {/* two columns */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16, marginTop: 18, alignItems: "start" }}>
        {/* form */}
        <div style={{ ...CARD, padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#063b48", marginBottom: 6 }}>{t("submit_title")}</div>
          <label style={lbl}>{t("subject")} *</label>
          <input style={inp} value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder={t("subject")} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>{t("req_type")} *</label>
              <select style={inp} value={form.request_type} onChange={(e) => set("request_type", e.target.value)}>{REQ_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label style={lbl}>{t("module")} *</label>
              <select style={inp} value={form.affected_module} onChange={(e) => set("affected_module", e.target.value)}>{MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
          <label style={lbl}>{t("priority")} *</label>
          <select style={inp} value={form.priority} onChange={(e) => set("priority", e.target.value)}>{PRIORITIES.map(([v, l, d]) => <option key={v} value={v}>{l} — {d}</option>)}</select>
          {form.priority === "critical" && (
            <div style={{ background: "#FBECEC", border: "1px solid #F3C6C6", color: "#8A2020", borderRadius: 9, padding: "8px 12px", fontSize: 12.5, marginTop: 8 }}>⚠ {t("crit_warn")}</div>
          )}
          <label style={lbl}>{t("desc")} *</label>
          <textarea style={{ ...inp, minHeight: 96, resize: "vertical" }} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("desc_ph")} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>{t("ref")}</label><input style={inp} value={form.reference_number} onChange={(e) => set("reference_number", e.target.value)} placeholder={t("ref_ph")} /></div>
            <div><label style={lbl}>{t("when")}</label><input type="datetime-local" style={inp} value={form.incident_at} onChange={(e) => set("incident_at", e.target.value)} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>{t("area")}</label>
              <select style={inp} value={form.affected_area} onChange={(e) => set("affected_area", e.target.value)}><option value="">—</option>{AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label style={lbl}>{t("device")}</label>
              <select style={inp} value={form.device_type} onChange={(e) => set("device_type", e.target.value)}><option value="">—</option>{DEVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
          <label style={lbl}>{t("users")}</label>
          <select style={inp} value={form.users_affected} onChange={(e) => set("users_affected", e.target.value)}><option value="">—</option>{USERS_AFFECTED.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#475569", marginTop: 12 }}>
            <input type="checkbox" checked={form.include_logs} onChange={(e) => set("include_logs", e.target.checked)} style={{ marginTop: 2 }} />
            {t("logs")}
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
            <button className="btn primary" disabled={busy} onClick={submit}>{busy ? t("submitting") : "✈ " + t("submit")}</button>
            {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
          </div>
        </div>

        {/* your requests */}
        <div style={{ ...CARD, padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#063b48", marginBottom: 10 }}>{t("yours")}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <input style={{ ...inp, width: "auto", flex: "1 1 140px" }} placeholder={t("search_tickets")} value={q} onChange={(e) => setQ(e.target.value)} />
            <select style={{ ...inp, width: "auto" }} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="">{t("all_status")}</option>{Object.keys(STATUS_LABEL).filter((s) => !["open", "in_progress"].includes(s)).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <select style={{ ...inp, width: "auto" }} value={fPrio} onChange={(e) => setFPrio(e.target.value)}>
              <option value="">{t("all_priority")}</option>{PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {rows == null ? <p className="muted">…</p> : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "26px 12px" }}>
              <div style={{ fontWeight: 600, color: "#475569" }}>{t("none")}</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t("none_sub")}</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map((tk) => (
                <div key={tk.id} onClick={() => openThread(tk.id)} style={{ border: "1px solid #eef2f2", borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span className="muted" style={{ fontSize: 12 }}>{tk.ticket_number}</span>
                    <Badge text={STATUS_LABEL[tk.status] || tk.status} color={STATUS_COLOR[tk.status] || "#8a949b"} />
                  </div>
                  <div style={{ fontWeight: 600, margin: "3px 0" }}>{tk.subject}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span className="muted" style={{ fontSize: 12.5 }}>{moduleLabel(tk.affected_module)}</span>
                    <Badge text={(tk.priority || "").toUpperCase()} color={PRIO_COLOR[tk.priority] || "#8a949b"} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* bottom resource cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 18 }}>
        {[["📖", "guides", "guides_d", "browse"], ["▶️", "videos", "videos_d", "watch"], ["✅", "status_c", "status_d", "view_status"], ["📣", "news", "news_d", "see_news"]].map(([ic, tt, dd, bb]) => (
          <div key={tt} style={{ ...CARD, padding: 16 }}>
            <div style={{ fontSize: 22 }}>{ic}</div>
            <div style={{ fontWeight: 700, color: "#063b48", margin: "6px 0 3px" }}>{t(tt)}</div>
            <div className="muted" style={{ fontSize: 12.5, minHeight: 34 }}>{t(dd)}</div>
            <a href="#resource" onClick={(e) => e.preventDefault()} style={{ fontSize: 13, color: "#0F6E56", fontWeight: 600, textDecoration: "none" }}>{t(bb)} →</a>
          </div>
        ))}
      </div>

      <div style={{ ...CARD, padding: "12px 18px", marginTop: 14, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontWeight: 700, color: "#063b48" }}>❓ {t("still")}</span>
        <span className="muted" style={{ fontSize: 13 }}>{t("still_d")}</span>
        <span className="muted" style={{ fontSize: 13, marginLeft: "auto" }}>✉ support@odra.app</span>
      </div>
    </div>
  );
}
